import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, Check, Copy, Save, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../stores/auth";
import { cn } from "../lib/utils";
import { desktopOnlyRoute } from "../components/ui/mobile-availability-page";

export const Route = createFileRoute("/settings/brm")({
  component: desktopOnlyRoute(BrmSettingsPage),
});

type Market = "DA" | "ID";
type Environment = "test" | "prod";
type TestStatus =
  | "ok"
  | "auth_failed"
  | "network_error"
  | "protocol_error"
  | "config_error"
  | null;

interface SlotStatus {
  market: Market;
  environment: Environment;
  configured: boolean;
  username: string | null;
  lastTestedAt: string | null;
  lastTestStatus: TestStatus;
  lastTestError: string | null;
  updatedAt: string | null;
}

interface BrmAccessDetails {
  ssoTestBaseUrl: string;
  ssoProdBaseUrl: string;
  auctionApiTestBaseUrl: string;
  auctionApiProdBaseUrl: string;
  intradayTradingWsTestUrl: string;
  intradayTradingWsProdUrl: string;
  intradayMarketDataWsTestUrl: string;
  intradayMarketDataWsProdUrl: string;
  daClientId: string;
  daScope: string;
  idClientId: string;
  idScope: string;
  memberId: string;
  memberEic: string;
  areaCode: string;
  portfolioDetails: string;
  apiEntitlements: string;
  ipAllowlist: string;
  supportContact: string;
  notes: string;
  daClientSecretConfigured: boolean;
  idClientSecretConfigured: boolean;
  updatedAt: string | null;
}

interface AccessDetailsResponse {
  details: BrmAccessDetails;
  requestText: string;
  canEdit: boolean;
}

type AccessDetailsPayload = Omit<
  BrmAccessDetails,
  "daClientSecretConfigured" | "idClientSecretConfigured" | "updatedAt"
> & {
  daClientSecret?: string;
  idClientSecret?: string;
  clearDaClientSecret?: boolean;
  clearIdClientSecret?: boolean;
};

const SLOTS: Array<{ market: Market; environment: Environment; label: string }> = [
  { market: "DA", environment: "test", label: "BRMDA test" },
  { market: "DA", environment: "prod", label: "BRMDA prod" },
  { market: "ID", environment: "test", label: "BRMID test" },
  { market: "ID", environment: "prod", label: "BRMID prod" },
];

function BrmSettingsPage() {
  const role = useAuth((s) => s.user?.role ?? null);
  const allowed = role === "company_admin" || role === "trader";
  const accessQuery = useQuery({
    queryKey: ["brm", "access-details"],
    queryFn: () => api<AccessDetailsResponse>("/brm/access-details"),
    enabled: allowed,
    retry: false,
  });
  const slotsQuery = useQuery({
    queryKey: ["brm", "credentials"],
    queryFn: () => api<{ slots: SlotStatus[] }>("/brm/credentials"),
    enabled: allowed,
    retry: false,
  });

  if (!allowed) {
    return <div className="p-8 text-sm text-muted">Not authorised.</div>;
  }

  if (slotsQuery.isLoading || accessQuery.isLoading) {
    return <div className="p-8 text-xs text-muted">loading...</div>;
  }

  if (slotsQuery.isError || accessQuery.isError) {
    const err = (accessQuery.error ?? slotsQuery.error) as Error;
    const disabled = err instanceof ApiError && err.status === 503;
    return (
      <div className="p-8 text-sm">
        {disabled ? (
          <p className="text-muted">BRM API integration is disabled.</p>
        ) : (
          <p className="text-negRed">Failed to load BRM settings: {err.message}</p>
        )}
      </div>
    );
  }

  const byKey = new Map<string, SlotStatus>();
  for (const slot of slotsQuery.data?.slots ?? []) {
    byKey.set(`${slot.market}:${slot.environment}`, slot);
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <header className="mb-5 flex items-center justify-between gap-3">
        <h1 className="text-lg font-semibold">BRM API Access</h1>
      </header>
      {accessQuery.data ? (
        <AccessDetailsPanel
          details={accessQuery.data.details}
          requestText={accessQuery.data.requestText}
          canEdit={accessQuery.data.canEdit}
        />
      ) : null}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SLOTS.map((item) => (
          <SlotCard
            key={`${item.market}:${item.environment}`}
            label={item.label}
            market={item.market}
            environment={item.environment}
            slot={
              byKey.get(`${item.market}:${item.environment}`) ?? {
                market: item.market,
                environment: item.environment,
                configured: false,
                username: null,
                lastTestedAt: null,
                lastTestStatus: null,
                lastTestError: null,
                updatedAt: null,
              }
            }
          />
        ))}
      </div>
    </div>
  );
}

const ACCESS_FIELD_GROUPS: Array<{
  title: string;
  fields: Array<{ key: keyof AccessDetailsPayload; label: string; multiline?: boolean }>;
}> = [
  {
    title: "SSO",
    fields: [
      { key: "ssoTestBaseUrl", label: "SSO test URL" },
      { key: "ssoProdBaseUrl", label: "SSO prod URL" },
    ],
  },
  {
    title: "DA",
    fields: [
      { key: "daClientId", label: "DA client ID" },
      { key: "daScope", label: "DA scope" },
      { key: "auctionApiTestBaseUrl", label: "DA API test URL" },
      { key: "auctionApiProdBaseUrl", label: "DA API prod URL" },
    ],
  },
  {
    title: "ID",
    fields: [
      { key: "idClientId", label: "ID client ID" },
      { key: "idScope", label: "ID scope" },
      { key: "intradayTradingWsTestUrl", label: "ID trading WS test" },
      { key: "intradayTradingWsProdUrl", label: "ID trading WS prod" },
      { key: "intradayMarketDataWsTestUrl", label: "ID market-data WS test" },
      { key: "intradayMarketDataWsProdUrl", label: "ID market-data WS prod" },
    ],
  },
  {
    title: "Company",
    fields: [
      { key: "memberId", label: "Member ID" },
      { key: "memberEic", label: "Member EIC" },
      { key: "areaCode", label: "Area code" },
      { key: "portfolioDetails", label: "Portfolio IDs / names", multiline: true },
      { key: "apiEntitlements", label: "Entitlements" },
      { key: "ipAllowlist", label: "IP allowlist" },
      { key: "supportContact", label: "Support contact" },
      { key: "notes", label: "Notes", multiline: true },
    ],
  },
];

function AccessDetailsPanel({
  details,
  requestText,
  canEdit,
}: {
  details: BrmAccessDetails;
  requestText: string;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<AccessDetailsPayload>(() => payloadFromDetails(details));
  const [daClientSecret, setDaClientSecret] = useState("");
  const [idClientSecret, setIdClientSecret] = useState("");
  const [clearDaClientSecret, setClearDaClientSecret] = useState(false);
  const [clearIdClientSecret, setClearIdClientSecret] = useState(false);
  const [copied, setCopied] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setForm(payloadFromDetails(details));
    setDaClientSecret("");
    setIdClientSecret("");
    setClearDaClientSecret(false);
    setClearIdClientSecret(false);
  }, [details]);

  const saveMutation = useMutation({
    mutationFn: () =>
      api<AccessDetailsResponse>("/brm/access-details", {
        method: "PUT",
        body: JSON.stringify({
          ...form,
          daClientSecret,
          idClientSecret,
          clearDaClientSecret,
          clearIdClientSecret,
        }),
      }),
    onSuccess: () => {
      setDaClientSecret("");
      setIdClientSecret("");
      setClearDaClientSecret(false);
      setClearIdClientSecret(false);
      setLocalError(null);
      void queryClient.invalidateQueries({ queryKey: ["brm", "access-details"] });
    },
    onError: (err) => setLocalError(errorText(err)),
  });

  const setField = (key: keyof AccessDetailsPayload, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const copyRequest = async () => {
    await navigator.clipboard.writeText(requestText);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };

  return (
    <section className="mb-5 border border-border/60 rounded-md p-4 bg-bg">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Access pack</h2>
          <p className="mt-1 text-xs text-muted">
            updated {formatRelative(details.updatedAt)}
          </p>
        </div>
        {canEdit ? (
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className={buttonClass(saveMutation.isPending, "accent")}
          >
            <Save className="h-3.5 w-3.5" />
            {saveMutation.isPending ? "Saving" : "Save"}
          </button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {ACCESS_FIELD_GROUPS.map((group) => (
          <div key={group.title} className="space-y-3">
            <h3 className="text-xs font-semibold uppercase text-muted">{group.title}</h3>
            {group.fields.map((field) => (
              <AccessField
                key={field.key}
                label={field.label}
                value={String(form[field.key] ?? "")}
                multiline={field.multiline}
                disabled={!canEdit || saveMutation.isPending}
                onChange={(value) => setField(field.key, value)}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <SecretField
          label="DA client secret"
          configured={details.daClientSecretConfigured}
          value={daClientSecret}
          clear={clearDaClientSecret}
          disabled={!canEdit || saveMutation.isPending}
          onValueChange={setDaClientSecret}
          onClearChange={setClearDaClientSecret}
        />
        <SecretField
          label="ID client secret"
          configured={details.idClientSecretConfigured}
          value={idClientSecret}
          clear={clearIdClientSecret}
          disabled={!canEdit || saveMutation.isPending}
          onValueChange={setIdClientSecret}
          onClearChange={setClearIdClientSecret}
        />
      </div>

      <div className="mt-4">
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <label className="text-xs text-muted">BRM request text</label>
          <button type="button" onClick={copyRequest} className={buttonClass(false)}>
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <textarea
          readOnly
          value={requestText}
          className="h-80 w-full resize-y rounded border border-border/60 bg-transparent px-3 py-2 font-mono text-xs leading-5 focus:border-accent focus:outline-none"
        />
      </div>

      {localError ? <p className="mt-3 break-all text-[11px] text-negRed">{localError}</p> : null}
    </section>
  );
}

function AccessField({
  label,
  value,
  multiline,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  multiline?: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block text-xs text-muted">
      <span className="mb-1 block">{label}</span>
      {multiline ? (
        <textarea
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          className="h-20 w-full resize-y rounded border border-border/60 bg-transparent px-2 py-1.5 text-sm text-fg focus:border-accent focus:outline-none disabled:text-muted"
        />
      ) : (
        <input
          type="text"
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          className="w-full rounded border border-border/60 bg-transparent px-2 py-1.5 text-sm text-fg focus:border-accent focus:outline-none disabled:text-muted"
        />
      )}
    </label>
  );
}

function SecretField({
  label,
  configured,
  value,
  clear,
  disabled,
  onValueChange,
  onClearChange,
}: {
  label: string;
  configured: boolean;
  value: string;
  clear: boolean;
  disabled: boolean;
  onValueChange: (value: string) => void;
  onClearChange: (value: boolean) => void;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs text-muted">{label}</label>
      <input
        type="password"
        value={value}
        disabled={disabled || clear}
        onChange={(event) => onValueChange(event.target.value)}
        autoComplete="new-password"
        placeholder={configured ? "custom secret set" : "server default"}
        className="w-full rounded border border-border/60 bg-transparent px-2 py-1.5 text-sm text-fg focus:border-accent focus:outline-none disabled:text-muted"
      />
      {configured ? (
        <label className="mt-2 inline-flex items-center gap-2 text-xs text-muted">
          <input
            type="checkbox"
            checked={clear}
            disabled={disabled}
            onChange={(event) => onClearChange(event.target.checked)}
            className="h-3.5 w-3.5 rounded border-border bg-transparent"
          />
          use server default
        </label>
      ) : null}
    </div>
  );
}

function payloadFromDetails(details: BrmAccessDetails): AccessDetailsPayload {
  return {
    ssoTestBaseUrl: details.ssoTestBaseUrl,
    ssoProdBaseUrl: details.ssoProdBaseUrl,
    auctionApiTestBaseUrl: details.auctionApiTestBaseUrl,
    auctionApiProdBaseUrl: details.auctionApiProdBaseUrl,
    intradayTradingWsTestUrl: details.intradayTradingWsTestUrl,
    intradayTradingWsProdUrl: details.intradayTradingWsProdUrl,
    intradayMarketDataWsTestUrl: details.intradayMarketDataWsTestUrl,
    intradayMarketDataWsProdUrl: details.intradayMarketDataWsProdUrl,
    daClientId: details.daClientId,
    daScope: details.daScope,
    idClientId: details.idClientId,
    idScope: details.idScope,
    memberId: details.memberId,
    memberEic: details.memberEic,
    areaCode: details.areaCode,
    portfolioDetails: details.portfolioDetails,
    apiEntitlements: details.apiEntitlements,
    ipAllowlist: details.ipAllowlist,
    supportContact: details.supportContact,
    notes: details.notes,
  };
}

function SlotCard({
  label,
  market,
  environment,
  slot,
}: {
  label: string;
  market: Market;
  environment: Environment;
  slot: SlotStatus;
}) {
  const queryClient = useQueryClient();
  const [username, setUsername] = useState(slot.username ?? "");
  const [password, setPassword] = useState("");
  const [replacePassword, setReplacePassword] = useState(!slot.configured);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setUsername(slot.username ?? "");
    setReplacePassword(!slot.configured);
  }, [slot.configured, slot.updatedAt, slot.username]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["brm", "credentials"] });

  const saveMutation = useMutation({
    mutationFn: () => {
      const body: { username: string; password?: string } = { username: username.trim() };
      if (replacePassword && password.length > 0) body.password = password;
      return api<{ slot: SlotStatus }>(`/brm/credentials/${market}/${environment}`, {
        method: "PUT",
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      setPassword("");
      setReplacePassword(false);
      setLocalError(null);
      void invalidate();
    },
    onError: (err) => setLocalError(errorText(err)),
  });

  const testMutation = useMutation({
    mutationFn: () =>
      api<{ status: TestStatus; error: string | null; slot: SlotStatus | null }>(
        `/brm/credentials/${market}/${environment}/test`,
        { method: "POST" }
      ),
    onSuccess: () => {
      setLocalError(null);
      void invalidate();
    },
    onError: (err) => setLocalError(errorText(err)),
  });

  const removeMutation = useMutation({
    mutationFn: () => api(`/brm/credentials/${market}/${environment}`, { method: "DELETE" }),
    onSuccess: () => {
      setUsername("");
      setPassword("");
      setReplacePassword(true);
      setLocalError(null);
      void invalidate();
    },
    onError: (err) => setLocalError(errorText(err)),
  });

  const busy = saveMutation.isPending || testMutation.isPending || removeMutation.isPending;
  const saveDisabled =
    busy ||
    username.trim().length === 0 ||
    (!slot.configured && password.length === 0) ||
    (replacePassword && password.length > 0 && password.length < 6);

  return (
    <section className="border border-border/60 rounded-md p-4 bg-bg">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">{label}</h2>
        <span
          className={cn(
            "shrink-0 rounded-full border px-2 py-0.5 text-[10px] uppercase",
            statusClass(slot.lastTestStatus)
          )}
        >
          {statusLabel(slot.lastTestStatus)}
        </span>
      </div>

      <label className="block text-xs text-muted mb-1">Username</label>
      <input
        type="text"
        value={username}
        onChange={(event) => setUsername(event.target.value)}
        autoComplete="off"
        className="mb-3 w-full rounded border border-border/60 bg-transparent px-2 py-1.5 text-sm focus:border-accent focus:outline-none"
      />

      <label className="block text-xs text-muted mb-1">Password</label>
      {slot.configured && !replacePassword ? (
        <div className="mb-3 flex items-center justify-between gap-2 text-xs">
          <span className="text-muted">password set</span>
          <button
            type="button"
            onClick={() => setReplacePassword(true)}
            className="text-accent hover:text-fg transition-colors"
          >
            replace
          </button>
        </div>
      ) : (
        <input
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          className="mb-3 w-full rounded border border-border/60 bg-transparent px-2 py-1.5 text-sm focus:border-accent focus:outline-none"
        />
      )}

      <div className="mb-3 flex items-center justify-between gap-2 text-[11px] text-muted">
        <span>tested {formatRelative(slot.lastTestedAt)}</span>
        {slot.updatedAt ? <span>updated {formatRelative(slot.updatedAt)}</span> : null}
      </div>

      {slot.lastTestError ? (
        <p className="mb-3 break-all text-[11px] text-negRed">{slot.lastTestError}</p>
      ) : null}
      {localError ? <p className="mb-3 break-all text-[11px] text-negRed">{localError}</p> : null}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => saveMutation.mutate()}
          disabled={saveDisabled}
          className={buttonClass(saveDisabled, "accent")}
        >
          <Save className="h-3.5 w-3.5" />
          {saveMutation.isPending ? "Saving" : "Save"}
        </button>
        <button
          type="button"
          onClick={() => testMutation.mutate()}
          disabled={busy || !slot.configured}
          className={buttonClass(busy || !slot.configured)}
        >
          <Activity className="h-3.5 w-3.5" />
          {testMutation.isPending ? "Testing" : "Test"}
        </button>
        <button
          type="button"
          onClick={() => {
            if (confirm(`Remove ${label} credentials?`)) removeMutation.mutate();
          }}
          disabled={busy || !slot.configured}
          className={buttonClass(busy || !slot.configured, "danger")}
        >
          <Trash2 className="h-3.5 w-3.5" />
          {removeMutation.isPending ? "Removing" : "Remove"}
        </button>
      </div>
    </section>
  );
}

function buttonClass(disabled: boolean, tone: "neutral" | "accent" | "danger" = "neutral"): string {
  return cn(
    "inline-flex h-8 items-center gap-1.5 rounded border px-3 text-xs transition-colors",
    disabled && "cursor-not-allowed border-border/40 text-muted",
    !disabled &&
      tone === "accent" &&
      "border-accent/60 text-accent hover:bg-accent/10",
    !disabled && tone === "danger" && "border-negRed/50 text-negRed hover:bg-negRed/10",
    !disabled && tone === "neutral" && "border-border text-fg hover:bg-fg/5"
  );
}

function statusLabel(status: TestStatus): string {
  switch (status) {
    case "ok":
      return "ok";
    case "auth_failed":
      return "auth failed";
    case "network_error":
      return "network";
    case "protocol_error":
      return "protocol";
    case "config_error":
      return "config";
    default:
      return "untested";
  }
}

function statusClass(status: TestStatus): string {
  switch (status) {
    case "ok":
      return "bg-posGreen/15 text-posGreen border-posGreen/30";
    case "auth_failed":
      return "bg-negRed/15 text-negRed border-negRed/30";
    case "network_error":
    case "protocol_error":
    case "config_error":
      return "bg-amber-500/15 text-amber-500 border-amber-500/30";
    default:
      return "bg-muted/15 text-muted border-muted/30";
  }
}

function formatRelative(iso: string | null): string {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(iso).toLocaleDateString();
}

function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body;
    if (body && typeof body === "object") {
      const maybe = (body as Record<string, unknown>).error;
      if (typeof maybe === "string") return maybe;
    }
    return err.message;
  }
  return (err as Error).message ?? String(err);
}
